'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ArrowLeft, Check, Globe2, Mail, ShieldCheck } from 'lucide-react';

import {
  EMAIL_AUTO_APPLY_COUNTRIES,
  EMAIL_AUTO_APPLY_COUNTRY_COUNT,
  safeSupportedCountriesReturnTo,
} from '@/config/supportedCountries';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatDeviceSetupCopy, getDeviceSetupContent, localizedCountryName } from '@/i18n/deviceSetupContent';

export default function SupportedCountriesClient({ returnTo }) {
  const { locale } = useLanguage();
  const copy = getDeviceSetupContent(locale).supportedCountries;
  const returnHref = safeSupportedCountriesReturnTo(returnTo);

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(124,108,248,0.2),_transparent_34%),linear-gradient(145deg,#f8f7ff,#ffffff_52%,#f5f2ff)] px-4 py-8 text-slate-900 sm:px-6 lg:py-14">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            href="/"
            aria-label="JOBolar.Ai home"
            className="inline-flex items-center rounded-xl bg-white/90 px-3 py-2 shadow-sm transition hover:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-200"
          >
            <Image src="/image/JobolarAiLogo.png" alt="JOBolar.Ai" width={160} height={48} priority className="h-9 w-auto sm:h-11" />
          </Link>
          <Link href={returnHref} className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-white px-4 py-2 text-xs font-bold text-[#5A4AF4] shadow-sm transition hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-200"><ArrowLeft size={15} aria-hidden="true" />{copy.back}</Link>
        </div>

        <header className="mx-auto mt-8 max-w-3xl text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#5A4AF4] to-violet-700 text-white shadow-xl shadow-violet-200"><Globe2 size={27} aria-hidden="true" /></span>
          <p className="mt-5 text-xs font-black uppercase tracking-[0.18em] text-[#5A4AF4]">{copy.eyebrow}</p>
          <h1 className="mt-3 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-5xl">{formatDeviceSetupCopy(copy.title, { count: EMAIL_AUTO_APPLY_COUNTRY_COUNT })}</h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">{copy.intro}</p>
        </header>

        <section className="mt-9 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {Object.entries(EMAIL_AUTO_APPLY_COUNTRIES).map(([region, countries]) => (
            <article key={region} className="rounded-[1.5rem] border border-white/90 bg-white/90 p-6 shadow-[0_22px_60px_rgba(49,42,119,0.12)] backdrop-blur">
              <h2 className="text-xl font-black text-slate-950">{copy.regions[region] || region}</h2>
              <p className="mt-1 text-xs font-bold text-[#5A4AF4]">{formatDeviceSetupCopy(countries.length === 1 ? copy.countryOne : copy.countryMany, { count: countries.length })}</p>
              <ul className="mt-5 grid gap-2 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2">
                {countries.map((country) => <li key={country} className="flex items-center gap-2 rounded-xl bg-violet-50/70 px-3 py-2 text-sm font-semibold text-slate-700"><Check className="shrink-0 text-emerald-500" size={15} aria-hidden="true" />{localizedCountryName(country, locale)}</li>)}
              </ul>
            </article>
          ))}
        </section>

        <section className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5"><h2 className="flex items-center gap-2 font-black text-amber-950"><Mail size={19} aria-hidden="true" />{copy.emailTitle}</h2><p className="mt-2 text-sm leading-6 text-amber-800">{copy.emailBody}</p></div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"><h2 className="flex items-center gap-2 font-black text-emerald-950"><ShieldCheck size={19} aria-hidden="true" />{copy.otherTitle}</h2><p className="mt-2 text-sm leading-6 text-emerald-800">{copy.otherBody}</p></div>
        </section>

        <div className="mt-8 text-center"><Link href={returnHref} className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#5A4AF4] to-violet-700 px-7 py-3.5 text-sm font-black text-white shadow-xl shadow-violet-200 transition hover:-translate-y-0.5">{copy.cta}</Link></div>
      </div>
    </main>
  );
}
